import * as React from "react";
import { Tabs as TabsPrimitive } from "@base-ui/react/tabs";

import { cn } from "../../lib/utils";

const TabsListVariantContext = React.createContext<
  "default" | "line" | "segmented"
>("default");

function Tabs({
  className,
  orientation = "horizontal",
  ...props
}: TabsPrimitive.Root.Props) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      className={cn(
        "flex gap-2 data-[orientation=horizontal]:flex-col",
        className,
      )}
      {...props}
    />
  );
}

type TabsListVariantProps = {
  variant?: "default" | "line" | "segmented";
};

function TabsList({
  className,
  variant = "default",
  ...props
}: TabsPrimitive.List.Props & TabsListVariantProps) {
  return (
    <TabsListVariantContext.Provider value={variant}>
      <TabsPrimitive.List
        data-slot="tabs-list"
        data-variant={variant}
        className={cn(
          "relative flex items-center gap-1 overflow-x-auto bg-transparent p-0",
          variant === "line" && "bg-transparent",
          variant === "segmented" &&
            "self-start gap-px overflow-x-visible rounded-[7px] bg-tint-tertiary p-0",
          className,
        )}
        {...props}
      />
    </TabsListVariantContext.Provider>
  );
}

function TabsTrigger({
  className,
  children,
  ...props
}: TabsPrimitive.Tab.Props) {
  const listVariant = React.useContext(TabsListVariantContext);

  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(
        "relative flex h-7 items-center whitespace-nowrap rounded-md border-0 border-transparent bg-transparent px-2.5 text-nav font-medium leading-nav text-text-secondary outline-none hover:bg-tint-secondary data-[hover]:bg-tint-secondary disabled:cursor-default disabled:bg-transparent disabled:opacity-50 data-[disabled]:cursor-default data-[disabled]:bg-transparent data-[disabled]:opacity-50 aria-disabled:cursor-default aria-disabled:bg-transparent aria-disabled:opacity-50 data-[selected]:bg-tint-secondary data-[selected]:text-text-primary data-[active]:bg-tint-secondary data-[active]:text-text-primary aria-selected:bg-tint-secondary aria-selected:text-text-primary focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:shadow-none data-[focus-visible]:ring-1 data-[focus-visible]:ring-ring data-[focus-visible]:ring-offset-2 data-[focus-visible]:ring-offset-background data-[focus-visible]:shadow-none [data-slot=tabs-list][data-variant=segmented]_&:h-auto [data-slot=tabs-list][data-variant=segmented]_&:flex-1 [data-slot=tabs-list][data-variant=segmented]_&:justify-center [data-slot=tabs-list][data-variant=segmented]_&:rounded-md [data-slot=tabs-list][data-variant=segmented]_&:border [data-slot=tabs-list][data-variant=segmented]_&:border-transparent [data-slot=tabs-list][data-variant=segmented]_&:px-3 [data-slot=tabs-list][data-variant=segmented]_&:py-1 [data-slot=tabs-list][data-variant=segmented]_&:bg-transparent [data-slot=tabs-list][data-variant=segmented]_&:text-text-secondary [data-slot=tabs-list][data-variant=segmented]_&:hover:bg-transparent [data-slot=tabs-list][data-variant=segmented]_&:hover:text-text-primary [data-slot=tabs-list][data-variant=segmented]_&[data-selected]:border-border-secondary [data-slot=tabs-list][data-variant=segmented]_&[data-active]:border-border-secondary [data-slot=tabs-list][data-variant=segmented]_&[aria-selected=true]:border-border-secondary [data-slot=tabs-list][data-variant=segmented]_&[data-selected]:bg-bg-elevated-transparent [data-slot=tabs-list][data-variant=segmented]_&[data-active]:bg-bg-elevated-transparent [data-slot=tabs-list][data-variant=segmented]_&[aria-selected=true]:bg-bg-elevated-transparent [data-slot=tabs-list][data-variant=segmented]_&[data-selected]:bg-clip-padding [data-slot=tabs-list][data-variant=segmented]_&[data-active]:bg-clip-padding [data-slot=tabs-list][data-variant=segmented]_&[aria-selected=true]:bg-clip-padding [data-slot=tabs-list][data-variant=segmented]_&[data-selected]:text-text-primary [data-slot=tabs-list][data-variant=segmented]_&[data-active]:text-text-primary [data-slot=tabs-list][data-variant=segmented]_&[aria-selected=true]:text-text-primary [data-slot=tabs-list][data-variant=segmented]_&[data-selected]:shadow-L1 [data-slot=tabs-list][data-variant=segmented]_&[data-active]:shadow-L1 [data-slot=tabs-list][data-variant=segmented]_&[aria-selected=true]:shadow-L1 [&>span]:flex [&>span]:flex-1 [&>span]:items-center [&>span]:gap-1.5 [&>span_span]:text-nav [&>span_span]:leading-nav [&>span_span]:text-text-disabled data-[selected]:[&>span_span]:text-text-secondary data-[active]:[&>span_span]:text-text-secondary aria-selected:[&>span_span]:text-text-secondary [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 data-[icon=inline-end]:pr-1 data-[icon=inline-start]:pl-1 [data-slot=tabs][data-orientation=vertical]_&:w-full [data-slot=tabs][data-orientation=vertical]_&:justify-start",
        listVariant === "segmented" &&
          "h-auto flex-1 justify-center rounded-md border border-transparent bg-transparent px-3 py-1 text-text-secondary hover:bg-transparent hover:text-text-primary data-[active]:!border-border-secondary data-[active]:!bg-bg-elevated-transparent data-[active]:!bg-clip-padding data-[active]:!text-text-primary data-[active]:!shadow-L1 aria-selected:!border-border-secondary aria-selected:!bg-bg-elevated-transparent aria-selected:!bg-clip-padding aria-selected:!text-text-primary aria-selected:!shadow-L1",
        className,
      )}
      {...props}
    >
      <span>{children}</span>
    </TabsPrimitive.Tab>
  );
}

function TabsContent({ className, ...props }: TabsPrimitive.Panel.Props) {
  return (
    <TabsPrimitive.Panel
      data-slot="tabs-content"
      className={cn("flex-1 text-sm leading-sm outline-none", className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
